import i18next from 'i18next';

export const getBgColorByParty = (party) => {
  switch (party) {
    case 'bn':
      return '#1b348e';
    case 'ph':
      return '#f9140d';
    case 'pn':
      return '#038901';
    case 'grs':
      return '#9eecff';
    case 'warisan':
      return '#383838ad';
    default:
      return '#ffff00';
  }
};

export const getFontColorByParty = (party) => {
  switch (party) {
    case 'bn':
    case 'ph':
    case 'pn':
    case 'warisan':
      return '#ffffff';
    case 'grs':
      return '#999999';
    default:
      return '#999999';
  }
};

// Backend icon filename convention (mirrors od_party_icon / OD_ICON_BASE), used
// only when the editor hasn't uploaded a logo — same URLs allPartyResult served.
const PARTY_ICON_BASE = 'https://the-oaks.my/demo/election/party/';
const conventionIcon = (code) =>
  code ? `${PARTY_ICON_BASE}${String(code).toLowerCase()}.png` : '';

const isWinner = (c) => Number(c.status) === 1 || Number(c.status) === 3;

const INDEPENDENT_LABEL = () =>
  (localStorage.getItem('lang') || 'zh_CN') === 'zh_TW' ? '獨立政黨' : '独立政党';

/**
 * Winner fill color per seat, derived from the seatinfo payload — replaces the
 * getInfo call on the seat-detail page. Same source column (alliances.color_hex)
 * and same '#8c8c8c' fallback as getInfo.php.
 * Accepts the seatinfo array or a seatname→seat map (lastData).
 */
export const deriveFillsFromSeats = (seats) => {
  const list = Array.isArray(seats) ? seats : Object.values(seats || {});
  const fills = {};
  list.forEach((seat) => {
    const winner = (seat.candidate || []).find(isWinner);
    if (winner) fills[seat.seatname] = winner.alliance_color || '#8c8c8c';
  });
  return fills;
};

/**
 * Coalition tally rows in the exact shape allPartyResult.php returned
 * ({key, party, icon, color, num, win, parties[]}), derived from the seatinfo
 * payload — replaces the allPartyResult call on the seat-detail page.
 * Mirrors od_build_coalition_rows: coalitions ordered by alliances.display_order
 * (the new alliance_order field), independents (no alliance) grouped last as
 * 独立政党 with the backend's #8c8c8c / empty-icon convention.
 */
export const derivePartyRows = (seats) => {
  const alliances = new Map(); // alliance_en -> {name, color, logo, order, parties: Map}
  (seats || []).forEach((seat) =>
    (seat.candidate || []).forEach((c) => {
      const aKey = c.alliance_en || ''; // '' = independent (no alliance)
      if (!alliances.has(aKey)) {
        alliances.set(aKey, {
          name: c.alliance || c.alliance_en || '',
          color: c.alliance_color || '',
          logo: c.alliance_logo || '',
          order: aKey === '' ? Infinity : Number(c.alliance_order ?? 9999),
          parties: new Map(),
        });
      }
      const a = alliances.get(aKey);
      const pKey = c.party_eng || c.party || '';
      if (!a.parties.has(pKey)) {
        a.parties.set(pKey, {
          partys: c.party || c.party_eng || '',
          icon: c.party_logo || conventionIcon(c.party_uid || c.party_eng),
          order: Number(c.party_order ?? 0),
          nums: 0,
          wins: 0,
        });
      }
      const p = a.parties.get(pKey);
      p.nums += 1;
      if (isWinner(c)) p.wins += 1;
    })
  );

  const sorted = [...alliances.entries()].sort(
    (x, y) => x[1].order - y[1].order || x[1].name.localeCompare(y[1].name, 'zh')
  );

  return sorted.map(([aKey, a], idx) => {
    const independent = aKey === '';
    const color = independent ? '#8c8c8c' : a.color || null;
    const subs = [...a.parties.values()]
      .sort((x, y) => x.order - y.order || x.partys.localeCompare(y.partys, 'zh'))
      .map((p, i) => ({
        key: i,
        color,
        partys: p.partys,
        icon: p.icon,
        nums: p.nums,
        wins: p.wins,
      }));
    return {
      key: idx,
      party: independent ? INDEPENDENT_LABEL() : a.name,
      icon: independent ? '' : a.logo || conventionIcon(aKey),
      color,
      num: subs.reduce((s, p) => s + p.nums, 0),
      win: subs.reduce((s, p) => s + p.wins, 0),
      parties: subs,
    };
  });
};

/**
 * Alliance bar-chart items in the exact shape chart.php returned
 * ([{name, colors, icon, value}]) — a projection of derivePartyRows, so the
 * home dashboard <Chart> can render from the seatinfo poll with no chart call.
 */
export const deriveChartData = (seats) =>
  derivePartyRows(seats).map((r) => ({
    name: r.party,
    colors: r.color,
    icon: r.icon,
    value: r.win,
  }));

export const getStatusById = (id) => {
  id = parseInt(id);
  switch (id) {
    case 1:
      return i18next.t('当选');
    case 2:
      return i18next.t('失按柜金');
    case 3:
      return i18next.t('不战而胜');
    case 4:
      return i18next.t('退选');
    default:
      return '';
  }
};
